import { Dictionary } from '@reduxjs/toolkit';
import { Account } from 'api/account';
import { constructFormData } from 'api/attachments/construct-form-data';
import { HttpClient } from 'api/http-client';
import { Company, Team, User, Session, SignupSession } from 'api/models';
import { UserPermission } from 'api/permissions';
import {
  UserCreationProps,
  UserProfileCreationProps,
} from 'contexts/project-creation';
import { Project } from '../project';

interface UserMetaData {
  profileSetupDone?: boolean;
  startedWithResourceManagement?: boolean;
}

export interface UserMeta {
  userId: number;
  metaData?: UserMetaData;
}

export type ProjectInUserProfile = Pick<
  Project,
  'projectId' | 'projectName' | 'visibility' | 'customer'
>;

export type UserProfileInfo = {
  company: Company;
  user: User;
  userMeta: UserMeta;
  permissions: UserPermission;
  teams: Team[];
  projects: ProjectInUserProfile[];
  /**
   * @deprecated
   * Do not use this account key. Instead get the object from useAccount();
   */
  account: Pick<Account, 'accountId' | 'accountName'>;
};

export type AvatarResponse = {
  location: string;
  thumbnailLocation: string;
};

interface BulkUser extends Pick<User, 'emailId' | 'userType'> {
  company: Pick<Company, 'companyId'>;
}

export type BulkUserCreatePayload = BulkUser[];

export interface HubSpotPayload {
  user: Dictionary<string | number>;
}

class UserHttpClient extends HttpClient {
  public async getProfile(): Promise<UserProfileInfo> {
    return this.get('/users/profile');
  }

  public async updateMeta(
    payload: Partial<UserMeta>,
  ): Promise<UserProfileInfo> {
    return this.put('/users/meta', payload);
  }

  public async updateCurrentUser(payload: Partial<User>): Promise<User> {
    return this.put('/users', payload);
  }

  public async getAllUsers(): Promise<User[]> {
    return this.get<User[]>('/users');
  }

  public async createUser(payload: UserCreationProps): Promise<User> {
    return this.post('/users', payload);
  }

  public async setupProfileWithToken(
    payload: UserProfileCreationProps,
  ): Promise<SignupSession> {
    return this.post(`/users/signup?token=${payload.token}`, payload);
  }

  public async save(
    payload: AtLeast<User, 'userId'>,
    token?: string,
  ): Promise<User> {
    if (token) {
      return this.put(`/users/${payload.userId}?token=${token}`, payload);
    }

    return this.put(`/users/${payload.userId}`, payload);
  }

  public async getDetailsFromToken(token: string): Promise<Session> {
    return this.post(`/users/auth?token=${token}`);
  }

  public getSession(token: string): Promise<{ sessionToken: string }> {
    return this.get('/users/get-session', {
      params: { token },
      headers: null,
    });
  }

  public getMagicLink(emailId: string): Promise<{ sessionToken: string }> {
    return this.get('/users/get-magic-link', {
      params: { emailId },
      headers: null,
    });
  }

  public async changePassword(payload: {
    user: AtLeast<User, 'userId'>;
    password: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    };
  }): Promise<User> {
    return this.post(`/users/reset/${payload.user.userId}`, payload.password);
  }

  public async resetPassword(payload: {
    newPassword: string;
    confirmPassword: string;
    token: string;
  }): Promise<{ sessionToken: string }> {
    // TODO: Remove the duplicate token param sending once api fixes it.
    return this.put(`/users/reset?token=${payload.token}`, payload);
  }

  public async forgotPassword(userId: number): Promise<User> {
    return this.post(`/users/forgot/${userId}`);
  }

  public async triggerResetPasswordEmail(email: string): Promise<User> {
    return this.post('/users/forgot', {
      emailId: email,
    });
  }

  public async uploadProfilePic(file: File): Promise<AvatarResponse> {
    const formData = constructFormData(file, {});

    return this.post('/users/photo', formData);
  }

  public async deactivateUser(userId: number): Promise<User> {
    return this.put(`/users/deactivate/${userId}`);
  }

  public async reactivateUser(userId: number): Promise<User> {
    return this.put(`/members/activate/${userId}`); // TODO: /users should slowly migrate to /members
  }

  public async bulkCreate(payload: BulkUserCreatePayload): Promise<User[]> {
    return this.post('/users/bulk-action', payload);
  }

  public async updateHubspot(payload: HubSpotPayload): Promise<void> {
    return this.post('/hubspot', payload);
  }

  public async logout() {
    return this.post('/users/logout');
  }

  public clearOktaSession = async () => this.post('/users/clear-session');
}

export const UserAPI = new UserHttpClient();
